import { logger } from '@/src/logging/logger';

export const authLifecycleChannelName = 'sparkone.auth.lifecycle';
export const authLifecycleVersion = 1 as const;
export const authLifecycleEnabled =
  process.env.NEXT_PUBLIC_AUTH_LIFECYCLE_ENABLED !== 'false';

export type AuthTerminalReason =
  | 'explicit_logout'
  | 'logout_all'
  | 'invalid_session';

type AuthLifecycleMessage = {
  version: typeof authLifecycleVersion;
  eventId: string;
  sourceTabId: string;
  occurredAt: number;
  kind: AuthTerminalReason;
};

type BroadcastChannelLike = {
  postMessage: (message: AuthLifecycleMessage) => void;
  close: () => void;
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
};

type Listener = (reason: AuthTerminalReason) => void;

const idPattern = /^[A-Za-z0-9_-]{16,64}$/;
const messageKeys = ['eventId', 'kind', 'occurredAt', 'sourceTabId', 'version'];
const maxRememberedEvents = 128;
const eventRetentionMs = 10 * 60_000;
const maxPastAgeMs = 2 * 60_000;
const maxFutureSkewMs = 30_000;

let channel: BroadcastChannelLike | null = null;
let channelAttempted = false;
let sourceTabId: string | null = null;
const listeners = new Set<Listener>();
const seenEvents = new Map<string, number>();

function randomId(): string {
  try {
    const randomUUID = globalThis.crypto?.randomUUID;
    if (typeof randomUUID === 'function') {
      return randomUUID.call(globalThis.crypto).replaceAll('-', '');
    }
    const bytes = new Uint8Array(16);
    globalThis.crypto?.getRandomValues(bytes);
    if (bytes.some((byte) => byte !== 0)) {
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    // Fall through to a document-local random identifier.
  }
  const seed = `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  return seed.padEnd(32, '0').slice(0, 32);
}

function currentSourceTabId(): string {
  sourceTabId ??= randomId();
  return sourceTabId;
}

function isTerminalReason(value: unknown): value is AuthTerminalReason {
  return value === 'explicit_logout' || value === 'logout_all' || value === 'invalid_session';
}

function pruneSeenEvents(now: number): void {
  for (const [eventId, observedAt] of seenEvents) {
    if (now - observedAt > eventRetentionMs) seenEvents.delete(eventId);
  }
  while (seenEvents.size > maxRememberedEvents) {
    const oldest = seenEvents.keys().next().value as string | undefined;
    if (!oldest) break;
    seenEvents.delete(oldest);
  }
}

function lifecycleTelemetryContext(outcome: string) {
  return { errorCategory: `browser.auth.lifecycle.${outcome}` };
}

export function parseAuthLifecycleMessage(
  value: unknown,
  now = Date.now(),
): AuthLifecycleMessage | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record).sort();
  if (
    keys.length !== messageKeys.length ||
    keys.some((key, index) => key !== messageKeys[index])
  ) return null;
  if (record.version !== authLifecycleVersion) return null;
  if (typeof record.eventId !== 'string' || !idPattern.test(record.eventId)) return null;
  if (typeof record.sourceTabId !== 'string' || !idPattern.test(record.sourceTabId)) return null;
  if (typeof record.occurredAt !== 'number' || !Number.isSafeInteger(record.occurredAt)) return null;
  if (record.occurredAt < now - maxPastAgeMs || record.occurredAt > now + maxFutureSkewMs) return null;
  if (!isTerminalReason(record.kind)) return null;
  return record as AuthLifecycleMessage;
}

function handleMessage(value: unknown): void {
  const now = Date.now();
  const message = parseAuthLifecycleMessage(value, now);
  if (!message) {
    logger.warn('sparkone.auth.lifecycle.ignored', lifecycleTelemetryContext('ignored.invalid_message'));
    return;
  }
  if (message.sourceTabId === currentSourceTabId()) {
    logger.info('sparkone.auth.lifecycle.ignored', lifecycleTelemetryContext('ignored.reflection'));
    return;
  }
  pruneSeenEvents(now);
  if (seenEvents.has(message.eventId)) {
    logger.info('sparkone.auth.lifecycle.ignored', lifecycleTelemetryContext('ignored.duplicate'));
    return;
  }
  seenEvents.set(message.eventId, now);
  pruneSeenEvents(now);
  logger.info('sparkone.auth.lifecycle.received', lifecycleTelemetryContext('received'));
  for (const listener of listeners) {
    try {
      listener(message.kind);
    } catch {
      logger.warn('sparkone.auth.lifecycle.ignored', lifecycleTelemetryContext('ignored.listener_failed'));
    }
  }
}

function ensureChannel(): BroadcastChannelLike | null {
  if (!authLifecycleEnabled || channelAttempted) return channel;
  channelAttempted = true;
  const Ctor = (globalThis as typeof globalThis & {
    BroadcastChannel?: new (name: string) => BroadcastChannelLike;
  }).BroadcastChannel;
  if (typeof Ctor !== 'function') {
    logger.info('sparkone.auth.lifecycle.fallback', lifecycleTelemetryContext('fallback.unavailable'));
    return null;
  }
  try {
    const createdChannel = new Ctor(authLifecycleChannelName);
    createdChannel.onmessage = (event) => handleMessage(event.data);
    channel = createdChannel;
  } catch {
    channel = null;
    logger.warn('sparkone.auth.lifecycle.fallback', lifecycleTelemetryContext('fallback.construction_failed'));
  }
  return channel;
}

export function publishAuthTerminalEvent(reason: AuthTerminalReason): void {
  const activeChannel = ensureChannel();
  if (!activeChannel) return;
  const message: AuthLifecycleMessage = {
    version: authLifecycleVersion,
    eventId: randomId(),
    sourceTabId: currentSourceTabId(),
    occurredAt: Date.now(),
    kind: reason,
  };
  try {
    activeChannel.postMessage(message);
    logger.info('sparkone.auth.lifecycle.sent', lifecycleTelemetryContext('sent'));
  } catch {
    logger.warn('sparkone.auth.lifecycle.fallback', lifecycleTelemetryContext('fallback.post_failed'));
  }
}

export function subscribeAuthTerminalEvents(listener: Listener): () => void {
  listeners.add(listener);
  ensureChannel();
  return () => listeners.delete(listener);
}

export function closeAuthLifecycleChannel(): void {
  const activeChannel = channel;
  channel = null;
  channelAttempted = false;
  if (!activeChannel) return;
  activeChannel.onmessage = null;
  try {
    activeChannel.close();
  } catch {
    logger.warn('sparkone.auth.lifecycle.fallback', lifecycleTelemetryContext('fallback.close_failed'));
  }
}

export function resetAuthLifecycleForTests(): void {
  closeAuthLifecycleChannel();
  listeners.clear();
  seenEvents.clear();
  sourceTabId = null;
}
